import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import OdooSOPreview from "components/shared/OdooSOPreview";
import { v4 } from "uuid";
import { SYNC_PLAN_TO_ODOO } from "../syncPlanToOdooViews/graphql";
import { FETCH_PLAN_SUMMARY_AND_ODOO_SO_DETAIL } from "./graphql";

const SyncPlanToSoView = ({ planId, soId, hide }) => {
  const { loading, error, data } = useQuery(
    FETCH_PLAN_SUMMARY_AND_ODOO_SO_DETAIL,
    {
      variables: { id: planId, soId },
      fetchPolicy: "network-only",
    },
  );
  const [send, sendRes] = useMutation(SYNC_PLAN_TO_ODOO, {
    variables: { planId, instance: "INC", orderType: "SO" },
    onCompleted() {
      Alert("success", "Plan sync to Odoo SO successfully");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
    refetchQueries: [FETCH_PLAN_SUMMARY_AND_ODOO_SO_DETAIL],
    awaitRefetchQueries: true,
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const extraData = JSON.parse(data.productionPlan.extraData || "{}");

  const addresses = [extraData.fobCustomer, ...extraData.fobCustomer.child_ids];
  const invoiceAddress = addresses.find(
    (i) => i.id === extraData.invoiceAddressId,
  );
  const deliveryAddress = addresses.find(
    (i) => i.id === extraData.deliveryAddressId,
  );

  const parsedOdooSo = data.odooSo ? JSON.parse(data.odooSo) : [];
  const odooSo = Array.isArray(parsedOdooSo) ? parsedOdooSo[0] : null;

  const orderLines = [
    ...data.productionPlan.lines,
    ...data.productionPlan.validChildren.flatMap((i) => i.lines),
  ].reduce((res, i) => {
    console.log("line", i);
    const found = res.find((x) => x.productId === i.product.odooIdInc);
    if (found)
      return res.map((x) =>
        x.id === found.id ? { ...x, qty: x.qty + i.finalQty } : x,
      );
    return [
      ...res,
      {
        id: v4(),
        productId: i.product.odooIdInc,
        name: `[${i.product.number}] ${i.product.name}`,
        qty: i.finalQty,
        odooQty: 0,
      },
    ];
  }, []);

  console.log("orderLines", orderLines);

  odooSo?.order_line?.forEach((line) => {
    const found = orderLines.find((x) => x.productId === line.product_id.id);
    if (found) {
      found.odooQty = line.product_uom_qty;
    } else {
      orderLines.push({
        id: v4(),
        productId: line.product_id.id,
        name: line.product_id.display_name,
        qty: 0,
        odooQty: line.product_uom_qty,
      });
    }
  });

  const isSynced = orderLines.every((line) => line.odooQty === line.qty);

  return (
    <div className="space-y-6 text-sm">
      <div className="card">
        <OdooSOPreview
          customerName={extraData.fobCustomer.name}
          invoiceAddress={invoiceAddress.contact_address_inline}
          deliveryAddress={deliveryAddress.contact_address_inline}
          warehouseName="Dropship"
          incoterms="FOB"
          orderLines={orderLines}
          hidePrice
          compareOdoo
        />
      </div>

      <div>
        {!odooSo ? (
          <div className="text-pink-600 mx-1 text-center font-semibold">
            ⚠️ SO not found in Odoo, cannot sync.
          </div>
        ) : odooSo.state === "draft" || odooSo.state === "sent" ? (
          isSynced ? (
            <div className="text-green-600 font-semibold">
              ✅ This plan is already Synced with Odoo SO: {odooSo.name}.
            </div>
          ) : (
            <Button primary onClick={send} loading={sendRes.loading}>
              Sync to Order {odooSo.name}
            </Button>
          )
        ) : (
          <div className="text-pink-600 mx-1 text-center font-semibold">
            <p>
              ⚠️ The order is not in editable state (Draft or Sent), cannot sync
              anymore.
            </p>
            <p>
              Please go to Odoo to cancel the order and create a new one if you
              want to sync the plan to Odoo SO again.
            </p>
            <p>If you have any question, please contact the support team.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncPlanToSoView;
